import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import * as FlightService from "_services/flight-service";
import { parseTime } from "../../../utils/date-time-functions";

const initialState = {
  flightInformation: null,
  flightInformationStatus: 'idle',
  flightInformationError: null,

  selectedFlight: null,
  routeUpdateResponse: null,

  flightAverage: null,
  flightAverageError: null,
  flightAverageStatus: 'idle',

  forecast: [],
  getForecastStatus: 'idle',
  getForecastError: null,

  createFlightScheduleStatus: 'idle',
  createFlightScheduleError: null,

  status: 'idle',
  error: null,

  patchFlightStatus: "idle",
  patchFlightError: null,
};

export const getFlightRouteForecast = createAsyncThunk("flightInformation/getFlightRouteForecast", async (params) => {
  return FlightService.getFlightRouteForecast(params);
}
);

export const getFlightInformation = createAsyncThunk("flightInformation/getFlightInformation", async (params) => {
  return FlightService.getFlightInformation(params);
});

export const refreshFlightRoute = createAsyncThunk("flightInformation/refreshFlightRoute", async (params) => {
  return FlightService.refreshFlightRoute(params);
});

export const getFlightAverage = createAsyncThunk("flightInformation/getFlightAverage", async (params) => {
  return FlightService.getFlightAverage(params);
});

export const updateFlightForecastRecord = createAsyncThunk("flightInformation/updateFlightForecastRecord", async (params) => {
  return FlightService.updateFlightForecastRecord(params);
});

export const createFlightSchedule = createAsyncThunk("flightInformation/createFlightSchedule", async (params) => {
  return FlightService.createFlightSchedule(params);
});

export const patchFlightSchedule = createAsyncThunk("flightInformation/patchFlightSchedule", async (params) => {
  return FlightService.patchFlight(params);
});

const flightInformationSlice = createSlice({
  name: "flightInformation",
  initialState,
  reducers: {
    reset: (state) => {
      state.flightInformation = null;
      state.selectedFlight = null;
      state.status = 'idle';
      state.error = null;
    },
    setSelectedFlight: (state, action) => {
      state.selectedFlight = action.payload;
    },
    resetFlightForecast: (state) => {
      state.forecast = [];
    },
    resetPatchflightFlags: (state) => {
      state.patchFlightError = null;
      state.patchFlightStatus = "idle";
    }
  },
  extraReducers: {
    [getFlightRouteForecast.pending]: (state, action) => {
      state.getForecastStatus = 'loading';
      state.getForecastError = null;
    },
    [getFlightRouteForecast.fulfilled]: (state, action) => {
      state.getForecastStatus = 'succeeded';
      state.forecast = action.payload;
    },
    [getFlightRouteForecast.rejected]: (state, action) => {
      state.getForecastStatus = 'failed';
      state.getForecastError = action.error.message;
    },
    [getFlightInformation.pending]: (state, action) => {
      state.flightInformationStatus = 'loading';
      state.flightInformationError = null;
    },
    [getFlightInformation.fulfilled]: (state, action) => {
      state.flightInformationStatus = 'succeeded';
      state.flightInformation = action.payload;
    },
    [getFlightInformation.rejected]: (state, action) => {
      state.flightInformationStatus = 'failed';
      state.flightInformationError = action.error.message;
    },
    [refreshFlightRoute.pending]: (state, action) => {
      state.status = 'loading';
    },
    [refreshFlightRoute.fulfilled]: (state, action) => {
      state.status = 'succeeded';
      state.routeUpdateResponse = action.payload;
    },
    [refreshFlightRoute.rejected]: (state, action) => {
      state.status = 'failed';
      state.error = action.error.message;
    },
    [getFlightAverage.pending]: (state, action) => {
      console.log('getting flight average...')
      state.flightAverageStatus = 'loading';
      state.flightAverageError = null;
    },
    [getFlightAverage.fulfilled]: (state, action) => {
      state.flightAverageStatus = 'succeeded';
      state.flightAverage = action.payload;
    },
    [getFlightAverage.rejected]: (state, action) => {
      state.flightAverageStatus = 'failed';
      state.flightAverageError = action.error.message;
    },
    [updateFlightForecastRecord.pending]: (state, action) => {
      state.status = 'loading';
    },
    [updateFlightForecastRecord.fulfilled]: (state, action) => {
      state.status = 'succeeded';
    },
    [updateFlightForecastRecord.rejected]: (state, action) => {
      state.status = 'failed';
      state.error = action.error.message;
    },
    [createFlightSchedule.pending]: (state, action) => {
      state.createFlightScheduleStatus = 'loading';
      state.createFlightScheduleError = null;
    },
    [createFlightSchedule.fulfilled]: (state, action) => {
      state.createFlightScheduleStatus = 'succeeded';
    },
    [createFlightSchedule.rejected]: (state, action) => {
      state.createFlightScheduleStatus = 'failed';
      state.createFlightScheduleError = action.error.message;
    },
    [patchFlightSchedule.pending]: (state, action) => {
      state.patchFlightStatus = 'loading';
    },
    [patchFlightSchedule.fulfilled]: (state, action) => {
      state.patchFlightStatus = 'succeeded';
    },
    [patchFlightSchedule.rejected]: (state, action) => {
      state.patchFlightStatus = 'failed';
      state.patchFlightError = action.error.message;
    },
  }
});

export const { reset, setSelectedFlight, resetFlightForecast, resetPatchflightFlags } = flightInformationSlice.actions;
export const selectSelectedFlight = (state) => state.flightInformation.selectedFlight;

// create flight record
export const selectFlightForecastRecordStatus = (state) => state.flightInformation.createFlightScheduleStatus
export const selectFlightForecastRecordError = (state) => state.flightInformation.createFlightScheduleError

export const selectFlightInformation = (state) => state.flightInformation.flightInformation;
export const selectRouteUpdateResponse = (state) => state.flightInformation.routeUpdateResponse

export const selectFlightAverage = (state) => state.flightInformation.flightAverage
export const selectFlightAverageError = (state) => state.flightInformation.flightAverageError
export const selectFlightAverageStatus = (state) => state.flightInformation.flightAverageStatus

export const selectFlightStatus = (state) => state.flightInformation.flightInformationStatus;
export const selectFlightError = (state) => state.flightInformation.flightInformationError;

function getDayOfWeek(dateString) {
  // Create a new Date object from the date string
  const date = new Date(dateString);

  // Array of weekday names
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Get the day of the week (0-6) from the Date object
  const dayIndex = date.getDay();

  // Return the name of the day
  return daysOfWeek[dayIndex];
}

export const selectFlightForecastStatus = (state) => state.flightInformation.getForecastStatus;
export const selectFlightForecastError = (state) => state.flightInformation.getForecastError;
export const selectPatchFlightStatus = state => state.flightInformation.patchFlightStatus
export const selectFlightForecast = (state) => {

  // process each flight into readable object
  let forecast = [...state.flightInformation.forecast];
  let formattedForecast = [];

  forecast.forEach((flightDocument) => {

    //console.log('flightDocument', flightDocument)

    // add day of week to flight document    

    // get hour from doc
    let hour = "";
    if (flightDocument.hour == null || flightDocument.hour == undefined) {
      return;
    }
    else {
      hour = parseTime(flightDocument.hour);
    }

    // format average so it is readable
    let average = flightDocument.forecastedPassengers ? flightDocument.forecastedPassengers : 0;
    let roundedAverage = average.toFixed(1)


    let formattedDoc = { ...flightDocument, 'day': getDayOfWeek(flightDocument.date), 'hour': hour, forecastedPassengers: roundedAverage };
    formattedForecast.push(formattedDoc);
  })

  // console.log('formattedForecast', formattedForecast)

  return formattedForecast;

}

export default flightInformationSlice.reducer;
